import {
  DndContext as DragAndDropContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import clsx from 'clsx';
import {
  useMemo,
  useState,
  type ReactNode,
  useRef,
  type RefObject,
} from 'react';

import Spacings from '@dynatrace/strato-design-tokens/spacings';

import {
  createRestrictColumnDragAndDrop,
  customCollisionDetection,
  handleDragEnd,
  indicateInsertPosition,
} from './column-order-utils.js';
import {
  dataTableV2ColumnDndOverlayCSS,
  verticalDividersCSS,
} from './ColumnOrder.sty.js';
import { useDataTableV2 } from '../../contexts/DataTableV2Context.js';

export function ColumnOrderDragAndDropContext({
  children,
}: {
  children: ReactNode;
}) {
  const table = useDataTableV2();
  const totalHeaderHeight = table._getTotalHeaderHeight();
  const containedVariant = table.getVisualVariantOptions().contained;
  const verticalDividers =
    table.getVisualVariantOptions().verticalDividers ||
    table.getHeaderGroups().length > 1;
  const virtualizationContainer = table.getVirtualizationContainer();
  const [dragOverlayContent, setDragOverlayContent] =
    useState<RefObject<HTMLDivElement> | null>(null);
  const [draggedHeaderSize, setDraggedHeaderSize] = useState<{
    width: number;
    height: number;
  }>({ width: 100, height: 37 });

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
  );

  const restrictColumnDragAndDrop = useMemo(
    () =>
      createRestrictColumnDragAndDrop(
        totalHeaderHeight,
        containedVariant,
        virtualizationContainer,
      ),
    [totalHeaderHeight, virtualizationContainer, containedVariant],
  );

  const placeholderElement = useRef<HTMLElement | null>(null);

  return (
    <DragAndDropContext
      sensors={sensors}
      collisionDetection={customCollisionDetection}
      modifiers={[restrictColumnDragAndDrop]}
      onDragStart={({ active }) => {
        table.setDraggedHeaderColumn(active.data.current?.column);
        setDraggedHeaderSize(table._getHeaderSize(active.id as string));
        setDragOverlayContent(active.data.current?.portalRef);

        // In order to make sure the header size does not change when the header
        // is portalled out, we create a placeholder element in the header in order
        // to preserve the grid layout
        const headerGroupElement =
          virtualizationContainer?.querySelector('[role="rowgroup"]');
        if (
          headerGroupElement === null ||
          !(headerGroupElement instanceof HTMLElement)
        ) {
          return;
        }
        const draggedHeaderElement = headerGroupElement.querySelector(
          `[data-columnid="${CSS.escape(String(active.id))}"]`,
        );
        if (
          draggedHeaderElement === null ||
          !(draggedHeaderElement instanceof HTMLElement)
        ) {
          return;
        }
        // Extract the position of the dragged element and its size in order
        // to position the placeholder.
        const { gridArea } = draggedHeaderElement.style;
        const { width, height } = draggedHeaderElement.getBoundingClientRect();
        if (gridArea) {
          const isFirstColumn =
            draggedHeaderElement.getAttribute('data-column') === '0';

          placeholderElement.current = document.createElement('div');
          placeholderElement.current.style.gridArea = gridArea;
          placeholderElement.current.style.width = `${width}px`;
          placeholderElement.current.style.height = `${height}px`;

          // ensure that vertical dividers are still visible
          if (verticalDividers && !isFirstColumn) {
            const isParentColumn =
              draggedHeaderElement.getAttribute('data-header-row') === '0';
            const heightOffset =
              headerGroupElement.offsetHeight -
              draggedHeaderElement.offsetHeight;

            placeholderElement.current.classList.add(verticalDividersCSS);
            placeholderElement.current.style.setProperty(
              '--column-dnd-placeholder-divider-top',
              !isParentColumn && heightOffset !== 0
                ? `${heightOffset}px`
                : Spacings.Size4,
            );
          }

          headerGroupElement.appendChild(placeholderElement.current);
        }
      }}
      onDragOver={(event) => {
        indicateInsertPosition(event, virtualizationContainer);
      }}
      onDragEnd={({ active, over }) => {
        handleDragEnd({
          active,
          over,
          table,
          virtualizationContainer,
        });
        if (placeholderElement.current) {
          placeholderElement.current.remove();
        }
      }}
    >
      <DragOverlay
        data-drag-overlay
        dropAnimation={null}
        className={clsx(dataTableV2ColumnDndOverlayCSS)}
        style={{
          // the observed header size includes a 2px border which we don't want in the overlay
          height: draggedHeaderSize.height - 2,
          width: draggedHeaderSize.width,
        }}
      >
        <div ref={dragOverlayContent} />
      </DragOverlay>
      {children}
    </DragAndDropContext>
  );
}
